import { observer } from "mobx-react-lite";

import { ItemRenderer, MeasuredItem } from "@/components/expression/inline-node/ItemRenderer";
import { GroupRowNode } from "@/components/expression/inline-node/wrapping/GroupFragment";
import { FlatNode } from "@/components/expression/utils/FlatNode";
import * as Node from "@/components/expression/utils/layout";

import type { RowNode } from "./types";
import type { InlineNodeContentRenderItem } from "../types";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

const RowNodeRenderer = observer(function RowNodeRenderer({
  node,
  onItemSize,
  suppressedCursor,
  externallyHandledTrailingFor,
}: {
  node: RowNode;
  onItemSize?: (key: string, width: number) => void;
  suppressedCursor?: SuppressedCursor;
  externallyHandledTrailingFor?: InlineNodeModel;
}) {
  if (node.kind === "items") {
    const { parentInline, items } = node;

    if (items.length === 0) {
      if (!parentInline || parentInline === externallyHandledTrailingFor) {
        return null;
      }

      const anchor = <FlatNode inlineNode={parentInline} index={0} />;

      if (onItemSize) {
        return (
          <MeasuredItem itemKey={`${parentInline.id}-empty`} onSize={onItemSize}>
            {anchor}
          </MeasuredItem>
        );
      }

      return anchor;
    }

    const reachesEnd = !!(parentInline && itemsReachInlineEnd(items, parentInline));
    const externallyHandled = !!parentInline && parentInline === externallyHandledTrailingFor;
    const trailingAnchor =
      reachesEnd && parentInline && !externallyHandled ? (
        <FlatNode key="trailing" inlineNode={parentInline} index={parentInline.nodes.length} />
      ) : null;

    return (
      <>
        {items.map((item, indexInRun) => {
          const suppress =
            !!suppressedCursor &&
            parentInline === suppressedCursor.parentInline &&
            item.inlineIndex === suppressedCursor.inlineIndex;

          return (
            <ItemRenderer
              key={item.key}
              item={item}
              parentInline={parentInline}
              isFirstInRow={indexInRun === 0}
              cursorPosition={suppress ? "none" : "start"}
              onItemSize={onItemSize}
            />
          );
        })}
        {trailingAnchor}
      </>
    );
  }

  if (node.kind === "comma") {
    const commaKey = `${node.container.id}-comma-${node.parameterIndex}`;
    const commaEl = <Node.Value className="self-center relative z-10">,</Node.Value>;

    if (!onItemSize) return commaEl;

    return (
      <MeasuredItem itemKey={commaKey} onSize={onItemSize}>
        {commaEl}
      </MeasuredItem>
    );
  }

  return (
    <GroupRowNode
      node={node}
      onItemSize={onItemSize}
      externallyHandledTrailingFor={externallyHandledTrailingFor}
    />
  );
});

type SuppressedCursor = {
  parentInline: InlineNodeModel;
  inlineIndex: number;
};

export type RowContentProps = {
  content: RowNode[];
  onItemSize?: (key: string, width: number) => void;
  /**
   * When set, the `FlatNode` rendered at this `(parentInline, inlineIndex)`
   * pair uses `cursorPosition="none"`. Used so that a sibling leading-filler
   * `FlatNode` can own the cursor anchor at that position without two cursors
   * rendering at the same point.
   */
  suppressedCursor?: SuppressedCursor;
  /**
   * When set, RowContent skips emitting trailing cursor anchors for items-runs
   * or group fragments whose `parentInline` matches this reference. The caller
   * (typically `Row`) takes responsibility for rendering that trailing
   * `FlatNode` itself (so it can attach `flex-1` for click-area fillers).
   * Nested-inline trailing anchors are unaffected.
   */
  externallyHandledTrailingFor?: InlineNodeModel;
};

export const RowContent = observer(function RowContent({
  content,
  onItemSize,
  suppressedCursor,
  externallyHandledTrailingFor,
}: RowContentProps) {
  return (
    <>
      {content.map((node, index) => (
        <RowNodeRenderer
          key={getRowNodeKey(node, index)}
          node={node}
          onItemSize={onItemSize}
          suppressedCursor={suppressedCursor}
          externallyHandledTrailingFor={externallyHandledTrailingFor}
        />
      ))}
    </>
  );
});

/**
 * Used to decide whether to render a trailing cursor-anchor `<FlatNode>` at
 * `parentInline.nodes.length` — the position where the cursor sits when it's
 * at the very end of the inline (e.g. before a closing bracket).
 */
function itemsReachInlineEnd(
  items: InlineNodeContentRenderItem[],
  parentInline: InlineNodeModel,
): boolean {
  if (items.length === 0) return false;

  const last = items[items.length - 1];
  const baseCount = last.node ? 1 : 0;
  const nextIndex = last.inlineIndex + baseCount + last.attachmentNodes.length;

  return nextIndex >= parentInline.nodes.length;
}

function getRowNodeKey(node: RowNode, fallbackIndex: number): string {
  if (node.kind === "items") {
    return node.items[0]?.key ?? `items-${fallbackIndex}`;
  }

  if (node.kind === "group") {
    return `${node.container.id}-${node.slice}`;
  }

  return `comma-${fallbackIndex}`;
}
